import React, { createContext, useState, useContext, useEffect } from "react";

import {
  ROLES,
  validatePhone,
  validateEmail,
  validatePassword,
  getUsers,
  getCurrentSessionUser,
  loginUser,
  registerUser,
  createStaffUser,
  deleteUserById,
  updateUserById,
  forgotPasswordUser,
  resetPasswordUser,
  sanitizeUser,
  normalizeUser,
} from "../Services/authService";

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const users = await getUsers();
      setUsersList(users);
      return users;
    } catch (error) {
      console.error("خطا در دریافت کاربران:", error);
      setUsersList([]);
      return [];
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      setAuthLoading(true);

      try {
        const savedToken = localStorage.getItem("authToken");
        const savedUser = localStorage.getItem("currentUser");

        if (savedToken && savedUser) {
          JSON.parse(savedUser);
          const freshUser = await getCurrentSessionUser();

          if (freshUser) {
            const safeUser = sanitizeUser(normalizeUser(freshUser));

            setUser(safeUser);
            setToken(savedToken);
            setIsAuthenticated(true);

            localStorage.setItem("currentUser", JSON.stringify(safeUser));
            if ([ROLES.SUPER_ADMIN, ROLES.STAFF].includes(safeUser.role)) await fetchUsers();
          } else {
            localStorage.removeItem("authToken");
            localStorage.removeItem("currentUser");
          }
        }
      } catch (error) {
        console.error("خطا در مقداردهی اولیه ورود:", error);
        localStorage.removeItem("authToken");
        localStorage.removeItem("currentUser");
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();
  }, []);

  const getAllCustomers = () => {
    return usersList
      .filter((u) => u.role === ROLES.CUSTOMER)
      .map((u) => ({
        id: u.id,
        username: u.username,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        phone: u.phone,
        email: u.email,
        role: u.role,
        createdAt: u.createdAt,
        ordersCount: u.orders?.length || 0,
        addressesCount: u.addresses?.length || 0,
      }));
  };

  const getAllStaff = () => {
    return usersList
      .filter((u) => u.role === ROLES.STAFF)
      .map((u) => ({
        id: u.id,
        username: u.username,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        phone: u.phone,
        email: u.email,
        role: u.role,
        permissions: u.permissions,
        createdAt: u.createdAt,
        createdBy: u.createdBy,
      }));
  };

  const getAllUsers = () => {
    return usersList.map((u) => ({
      id: u.id,
      username: u.username,
      firstName: u.firstName || "",
      lastName: u.lastName || "",
      phone: u.phone,
      email: u.email,
      role: u.role,
      createdAt: u.createdAt,
      ordersCount: u.orders?.length || 0,
      addressesCount: u.addresses?.length || 0,
    }));
  };

  const login = async (identifier, password) => {
    try {
      const result = await loginUser(identifier, password);

      if (!result.success) {
        return result;
      }

      setUser(result.user);
      setToken(result.token);
      setIsAuthenticated(true);

      localStorage.setItem("currentUser", JSON.stringify(result.user));
      localStorage.setItem("authToken", result.token);

      if ([ROLES.SUPER_ADMIN, ROLES.STAFF].includes(result.user.role)) await fetchUsers();

      return result;
    } catch (error) {
      console.error("خطا در ورود:", error);
      return {
        success: false,
        message: "خطا در ورود. لطفاً اتصال به دیتابیس را بررسی کنید.",
      };
    }
  };

  const register = async (username, phone, email, password) => {
    try {
      const result = await registerUser({
        username,
        phone,
        email,
        password,
      });

      if (result.success) {
        // ثبت‌نام کاربر عمومی نیازی به دریافت فهرست کاربران ندارد.
      }

      return result;
    } catch (error) {
      console.error("خطا در ثبت‌نام:", error);
      return {
        success: false,
        message: "خطا در ثبت‌نام. لطفاً اتصال به دیتابیس را بررسی کنید.",
      };
    }
  };

  const createStaff = async (username, phone, email, password) => {
    try {
      const result = await createStaffUser({
        username,
        phone,
        email,
        password,
        createdBy: user?.id || "system",
      });

      if (result.success) {
        await fetchUsers();
      }

      return result;
    } catch (error) {
      console.error("خطا در ایجاد کارمند:", error);
      return {
        success: false,
        message: "خطا در ایجاد کارمند. لطفاً اتصال به دیتابیس را بررسی کنید.",
      };
    }
  };

  const deleteUser = async (userId) => {
    try {
      const result = await deleteUserById(userId);

      if (result.success) {
        await fetchUsers();
      }

      return result;
    } catch (error) {
      console.error("خطا در حذف کاربر:", error);
      return {
        success: false,
        message: "خطا در حذف کاربر.",
      };
    }
  };

  const updateCurrentUser = async (updates) => {
    if (!user?.id) {
      return {
        success: false,
        message: "کاربر وارد حساب نشده است.",
      };
    }

    try {
      const updatedUser = await updateUserById(user.id, updates);

      setUser(updatedUser);
      localStorage.setItem("currentUser", JSON.stringify(updatedUser));

      if (isAdmin || isStaff) await fetchUsers();

      return {
        success: true,
        message: "اطلاعات کاربر با موفقیت به‌روزرسانی شد.",
        user: updatedUser,
      };
    } catch (error) {
      console.error("خطا در به‌روزرسانی کاربر:", error);
      return {
        success: false,
        message: "خطا در ذخیره اطلاعات کاربر.",
      };
    }
  };

  const refreshCurrentUser = async () => {
    if (!user?.id) return null;

    try {
      const freshUser = await getCurrentSessionUser();

      if (!freshUser) {
        logout();
        return null;
      }

      const safeUser = sanitizeUser(normalizeUser(freshUser));

      setUser(safeUser);
      localStorage.setItem("currentUser", JSON.stringify(safeUser));

      return safeUser;
    } catch (error) {
      console.error("خطا در دریافت مجدد اطلاعات کاربر:", error);
      return null;
    }
  };

  const forgotPassword = async (identifier) => {
    try {
      const result = await forgotPasswordUser(identifier);

      return result;
    } catch (error) {
      console.error("خطا در فراموشی رمز:", error);
      return {
        success: false,
        message: "خطا در فراموشی رمز عبور.",
      };
    }
  };

  const resetPassword = async (resetToken, newPassword) => {
    try {
      const result = await resetPasswordUser(resetToken, newPassword);

      return result;
    } catch (error) {
      console.error("خطا در بازنشانی رمز:", error);
      return {
        success: false,
        message: "خطا در بازنشانی رمز عبور.",
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);

    localStorage.removeItem("currentUser");
    localStorage.removeItem("authToken");

    return {
      success: true,
      message: "خروج با موفقیت انجام شد.",
    };
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === ROLES.SUPER_ADMIN) return true;

    return user.permissions?.includes(permission) || false;
  };

  const updateUser = async (userId, updates) => {
    try {
      const updatedUser = await updateUserById(userId, updates);

      await fetchUsers();

      return {
        success: true,
        message: "اطلاعات کاربر با موفقیت به‌روزرسانی شد.",
        user: updatedUser,
      };
    } catch (error) {
      console.error("خطا در به‌روزرسانی کاربر:", error);

      return {
        success: false,
        message: "خطا در ذخیره اطلاعات کاربر.",
      };
    }
  };

  const isAdmin = user?.role === ROLES.SUPER_ADMIN;
  const isStaff = user?.role === ROLES.STAFF;
  const isCustomer = user?.role === ROLES.CUSTOMER;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        usersList,
        isAuthenticated,
        authLoading,

        isAdmin,
        isStaff,
        isCustomer,

        ROLES,

        updateUser,

        hasPermission,

        login,
        logout,
        register,

        getAllUsers,
        getAllCustomers,
        getAllStaff,

        createStaff,
        deleteUser,

        updateCurrentUser,
        refreshCurrentUser,
        fetchUsers,

        forgotPassword,
        resetPassword,

        validatePhone,
        validateEmail,
        validatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
